import { mergeBVHFiles } from '@cartwhl/3d/bvh';
import { useEffect, useMemo } from 'react';

import type {
  PlayerAnimationSource,
  PlayerAssetLoadStatus,
  PlayerResolvedAnimation,
} from '../types';
import { usePlayerStoreApi } from '../store/playerStore';
import {
  normalizeBVHForPlayback,
  useBVHAnimation,
  useBVHAnimations,
} from './useBVHAnimation';

export interface UseResolvedPlayerAnimationOptions {
  actorId?: string;
  animation?: PlayerAnimationSource;
}

const INITIAL_RESOLVED_ANIMATION: PlayerResolvedAnimation = {
  kind: 'none',
  bvh: null,
  bvhs: [],
  status: 'initial',
  isLoading: false,
  isReady: false,
  error: null,
};
const EMPTY_BVH_URLS: string[] = [];

export function useResolvedPlayerAnimation({
  actorId,
  animation,
}: UseResolvedPlayerAnimationOptions): PlayerResolvedAnimation {
  const store = usePlayerStoreApi();
  const singleAnimation = useBVHAnimation({
    bvh: animation?.type === 'bvh' ? animation.bvh : undefined,
    bvhUrl: animation?.type === 'url' ? animation.url : undefined,
    rotationOffsets:
      animation?.type === 'url' ? animation.rotationOffsets : undefined,
  });
  const sequenceAnimation = useBVHAnimations({
    bvhUrls: animation?.type === 'sequence' ? animation.urls : EMPTY_BVH_URLS,
    rotationOffsets:
      animation?.type === 'sequence' ? animation.rotationOffsets : undefined,
  });
  const mergedSequenceBvh = useMemo(() => {
    if (animation?.type !== 'sequence' || sequenceAnimation.bvhs.length === 0) {
      return null;
    }

    return normalizeBVHForPlayback(mergeBVHFiles(sequenceAnimation.bvhs));
  }, [animation?.type, sequenceAnimation.bvhs]);

  const resolvedAnimation = useMemo<PlayerResolvedAnimation>(() => {
    if (!animation) {
      return INITIAL_RESOLVED_ANIMATION;
    }

    if (animation.type === 'sequence') {
      const status: PlayerAssetLoadStatus = sequenceAnimation.isLoading
        ? 'pending'
        : mergedSequenceBvh
          ? 'ready'
          : 'initial';

      return {
        kind: 'sequence',
        bvh: mergedSequenceBvh,
        bvhs: sequenceAnimation.bvhs,
        status,
        isLoading: status === 'pending',
        isReady: status === 'ready',
        error: sequenceAnimation.error,
      };
    }

    const status: PlayerAssetLoadStatus = singleAnimation.isLoading
      ? 'pending'
      : singleAnimation.bvh
        ? 'ready'
        : 'initial';

    return {
      kind: animation.type === 'bvh' || animation.type === 'url' ? 'single' : 'none',
      bvh: singleAnimation.bvh,
      bvhs: singleAnimation.bvh ? [singleAnimation.bvh] : [],
      status,
      isLoading: status === 'pending',
      isReady: status === 'ready',
      error: singleAnimation.error,
    };
  }, [
    animation,
    mergedSequenceBvh,
    sequenceAnimation.error,
    sequenceAnimation.isLoading,
    singleAnimation.bvh,
    singleAnimation.error,
    singleAnimation.isLoading,
  ]);

  useEffect(() => {
    if (!actorId) {
      return;
    }

    store.setActorAnimationStatus(actorId, resolvedAnimation.status);
    store.setActorAssetError(actorId, resolvedAnimation.error);
    store.setResolvedAnimation(actorId, resolvedAnimation);
  }, [actorId, resolvedAnimation, store]);

  useEffect(() => {
    if (!actorId) {
      return;
    }

    return () => {
      store.clearResolvedAnimation(actorId);
    };
  }, [actorId, store]);

  return resolvedAnimation;
}
